import { useMemo, useState } from "react"
import { Card, Container, Row, Col } from "react-bootstrap"
import { AdTagChips } from "src/modules/engagement/components/adTags/AdTagChips"
import type { EngagementAdTags } from "@services/engagements/commands/cmdGetEngagementAdTags"
import { count, money } from "../../performanceSnapshot"
import { type AdRow, type LeaderboardBasis, adRows, leaderboard } from "./adMetrics"
import AdFlightChart from "./charts/AdFlightChart"

/*
    Ads tab (MPN-1530 §6).

    Page order is the ticket's: Top Performers, then Conversion Trend with the flight
    bars, then the individual ad tiles. It used to open on the tiles, which is the detail
    view before the summary.

    The By Conversions toggle is live rather than hidden. §6.2 says to keep it disabled
    until the attribution in MPN-1531 ships, and it ships in this same change, so the
    toggle has something real to rank on. It is labelled "conversions during flight"
    throughout, as MPN-1531 requires, because time-window overlap is not causation.
*/

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="d-flex justify-content-between align-items-center">
    <span className="fs-12 text-muted">{label}</span>
    <span className="fs-12 fw-semibold">{value}</span>
  </div>
)

const AdPerformance = ({ data, adTags = {} }: { data: any; adTags?: EngagementAdTags }) => {
  const [basis, setBasis] = useState<LeaderboardBasis>("engagements")

  const rows = useMemo(() => adRows(data), [data])
  const top = useMemo(() => leaderboard(rows, basis), [rows, basis])

  // Only offer the conversions ranking when the attribution actually returned something;
  // otherwise the toggle promises a view that would rank every ad at zero.
  const canRankByConversions = rows.some((r) => r.conversionsDuringFlight !== null)

  if (rows.length === 0) {
    return (
      <Container className="mb-5">
        <Card className="custom-card border border-2 mb-0">
          <Card.Body>
            <p className="text-muted mb-0">No ad performance data in this date range.</p>
          </Card.Body>
        </Card>
      </Container>
    )
  }

  return (
    <Container className="d-flex flex-column gap-3 mb-5" style={{ minHeight: 364 }}>
      <Card className="custom-card border border-2 mb-0">
        <Card.Header className="justify-content-between">
          <Card.Title>
            Top Performers{" "}
            <span className="mon-panel__tag">
              ranked by {basis === "conversions" ? "conversions during flight" : "engagements"}
            </span>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          {canRankByConversions && (
            <div className="mon-grain">
              <button
                type="button"
                className={`mon-grain__btn${basis === "engagements" ? " mon-grain__btn--on" : ""}`}
                onClick={() => setBasis("engagements")}
              >
                By Engagements
              </button>
              <button
                type="button"
                className={`mon-grain__btn${basis === "conversions" ? " mon-grain__btn--on" : ""}`}
                onClick={() => setBasis("conversions")}
              >
                By Conversions
              </button>
            </div>
          )}

          {/*
              A proportional bar per row, as the supplied mockup has it. The top three are
              often close (225 / 207 / 200 in the mockup's own data) and three numbers that
              near each other do not read as a ranking in text alone. The bar is scaled to
              the leader, so first place is always full width and the rest are read against
              it.
          */}
          <div className="mon-leaderboard">
            {top.map((ad, index) => {
              const value = basis === "conversions"
                ? (ad.conversionsDuringFlight ?? 0)
                : ad.engagements
              const leader = basis === "conversions"
                ? (top[0]?.conversionsDuringFlight ?? 0)
                : (top[0]?.engagements ?? 0)
              // Guard the leader being zero: an empty range should draw no bars rather
              // than divide by zero and render NaN% widths.
              const pct = leader > 0 ? Math.max((value / leader) * 100, 2) : 0
              return (
                <div className="mon-leaderboard__item" key={ad.ad_id}>
                  <span className="mon-leaderboard__rank">{index + 1}</span>
                  <span className="mon-leaderboard__name">{ad.ad_name}</span>
                  <span className="mon-leaderboard__track">
                    <span
                      className="mon-leaderboard__bar"
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="mon-leaderboard__value">
                    {count(value)}
                    <span className="mon-leaderboard__unit">
                      {basis === "conversions" ? "during flight" : "engagements"}
                    </span>
                  </span>
                </div>
              )
            })}
          </div>

          {basis === "conversions" && (
            <p className="mon-panel__foot">
              Conversions during flight counts every conversion that happened while the ad
              was scheduled. Ads running the same days each count the same conversion, so
              these do not add up to the campaign total and do not say the ad caused it.
            </p>
          )}
        </Card.Body>
      </Card>

      <Card className="custom-card border border-2 mb-0">
        <Card.Header>
          <Card.Title>Conversion Trend + Which Ads Were Running</Card.Title>
        </Card.Header>
        <Card.Body>
          <AdFlightChart data={data} />
        </Card.Body>
      </Card>

      <Row className="g-3">
        {rows.map((ad: AdRow) => (
          <Col xs={12} sm={6} md={4} lg={3} key={ad.ad_id}>
            <Card className="custom-card border border-2 h-100">
              <Card.Body>
                <Card.Title className="fs-14 fw-semibold mb-1 text-truncate">
                  {ad.ad_name}
                </Card.Title>
                {/* Content Catalog tags (MPN-1506). */}
                <div className="mb-3">
                  <AdTagChips groups={adTags[ad.ad_id]} />
                </div>
                <div
                  className="d-flex flex-column gap-2"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  <Stat label="Type" value={ad.ad_type || "N/A"} />
                  <Stat label="Spend" value={money(ad.spend)} />
                  <Stat label="Impressions" value={count(ad.impressions)} />
                  <Stat label="Clicks" value={count(ad.clicks)} />
                  <Stat label="Engagements" value={count(ad.engagements)} />
                  {/* §6.1 */}
                  <Stat label="CPC" value={ad.cpc} />
                  <Stat label="CPM" value={ad.cpm} />
                  <Stat label="CTR" value={ad.ctr} />
                  <Stat label="Days Scheduled" value={count(ad.daysScheduled)} />
                  <Stat label="Avg Daily Impressions" value={ad.avgDailyImpressions} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Container>
  )
}

export default AdPerformance
